
(function() {
    /**
     * Delta card battle ability functions.
     * Each function is keyed by the DeltaCard UUID from the database.
     * Structure mirrors abilities.js: modifiers, initial, turn, move, damaged.
     *
     * UUID map (matches DeltaCard.id seeded in create_delta_card_table_seed.sql):
     * D001 Delta Stream          => D001-0000-0000-0000-000000000001
     * D002 Apex Reservoir        => D002-0000-0000-0000-000000000002
     * ... (all 200)
     *
     * Functions receive: (team, team1, team2, index1, index2 [, damage])
     * team  = active team (the card's team)
     * team1 = team of the card using the delta ability
     * team2 = opposing team
     * index1 = index of active card in team1
     * index2 = index of active card in team2
     */

    window.deltaAbilities = {

        // ─────────────────────────────────────────────
        // MODIFIERS — passive stat/behaviour overrides
        // ─────────────────────────────────────────────
        modifiers: {

            // Check all delta modifiers for a card
            card(card, team1, team2, index1, index2) {
                // Capbreaker (D009): stat caps +20% — applied at stat-cap evaluation layer
                // Calculated Odds (D004): ability proc +10% — applied at proc-roll layer
            },

            // D009 Capbreaker: all stat caps increased by 20%
            statCap(card, baseCap) {
                if (card.deltaCardKey === 'D009-0000-0000-0000-000000000009') {
                    return Math.round(baseCap * 1.20);
                }
                return baseCap;
            },

            // D004 Calculated Odds: all ability proc chances +10%
            procChance(card, chance) {
                if (card.deltaCardKey === 'D004-0000-0000-0000-000000000004') {
                    return Math.min(chance + 10, 100);
                }
                return chance;
            },

            // D168 Loaded Coin: 10% → 12%, 20% → 18%
            adjustChance(card, chance) {
                if (card.deltaCardKey === 'D168-0000-0000-0000-000000000168') {
                    if (chance === 10) return 12;
                    if (chance === 20) return 18;
                }
                return chance;
            },

            // D054 Feather Trigger: >120 speed → +10% crit chance
            critChance(card, base) {
                if (card.deltaCardKey === 'D054-0000-0000-0000-000000000054' && card.speed > 120) {
                    return base + 10;
                }
                // D055 Measured Impact: <50 speed → crits deal +20% damage (tracked via critDamage modifier)
                return base;
            },

            // D052 Clean Window: no enemy buffs → +12% crit chance
            cleanWindowCrit(card, enemyCard, base) {
                if (card.deltaCardKey === 'D052-0000-0000-0000-000000000052') {
                    const hasBuffs = enemyCard.buffs && Object.keys(enemyCard.buffs).length > 0;
                    if (!hasBuffs) return base + 12;
                }
                return base;
            },

            // D058 Razor Calm: no negative effects → +10% accuracy + crit chance
            razorCalm(card, stat) {
                if (card.deltaCardKey === 'D058-0000-0000-0000-000000000058') {
                    const hasNegative = card.statuses && card.statuses.length > 0;
                    if (!hasNegative) return stat + 10;
                }
                return stat;
            },

            // D110 Bastion Fold: >80% HP → +10% armour and ward until end of turn
            bastionFold(card, stat) {
                if (card.deltaCardKey === 'D110-0000-0000-0000-000000000110') {
                    const hpPct = (card.hp / card.maxHp) * 100;
                    if (hpPct > 80) return Math.round(stat * 1.10);
                }
                return stat;
            },

            // D111 Glass Contract: <30% HP → -10% defence, +20% accuracy and crit
            glassContract(card, stat, statType) {
                if (card.deltaCardKey === 'D111-0000-0000-0000-000000000111') {
                    const hpPct = (card.hp / card.maxHp) * 100;
                    if (hpPct < 30) {
                        if (statType === 'defence') return Math.round(stat * 0.90);
                        if (statType === 'accuracy' || statType === 'critChance') return stat + 20;
                    }
                }
                return stat;
            },

            // D101 Ward Bulwark: Ward also reduces Physical damage by 25%
            wardVsPhysical(card, damage) {
                if (card.deltaCardKey === 'D101-0000-0000-0000-000000000101') {
                    const wardReduction = Math.round(card.ward * 0.25);
                    return Math.max(0, damage - wardReduction);
                }
                return damage;
            },

            // D107 Dense Hide: Armour > Ward → -10% crit damage taken
            denseHide(card, critDmg) {
                if (card.deltaCardKey === 'D107-0000-0000-0000-000000000107' && card.armor > card.ward) {
                    return Math.round(critDmg * 0.90);
                }
                return critDmg;
            },

            // D118 Necrofilm: any negative status → +10% Ward
            necrofilm(card) {
                if (card.deltaCardKey === 'D118-0000-0000-0000-000000000118') {
                    const hasNegative = card.statuses && card.statuses.length > 0;
                    if (hasNegative) return Math.round(card.ward * 1.10);
                }
                return card.ward;
            },

            // D046 Zeroed Scope: first attack per card cannot miss due to accuracy penalties
            zeroedScope(card) {
                return card.deltaCardKey === 'D046-0000-0000-0000-000000000046' && !card._firstAttackDone;
            },

            // D015 Conservation Law: basic attacks cost 1 less Stamina, magic 1 less Mana (min 1)
            resourceCost(card, cost, type) {
                if (card.deltaCardKey === 'D015-0000-0000-0000-000000000015') {
                    return Math.max(1, cost - 1);
                }
                return cost;
            }
        },

        // ─────────────────────────────────────────────
        // INITIAL — on-battle-start effects
        // ─────────────────────────────────────────────
        initial: {

            // D010 Frontline Investment: +1000 Attack for first allied card
            'D010-0000-0000-0000-000000000010'(team1, team2, index1, index2) {
                if (index1 === 0) {
                    team1[0].attack += 1000;
                }
            },

            // D017 Reactor Start: second allied card enters with +25 Stamina and +25 Mana
            'D017-0000-0000-0000-000000000017'(team1, team2, index1, index2) {
                if (team1.length > 1) {
                    team1[1].stamina = Math.min((team1[1].stamina || 0) + 25, 100);
                    team1[1].mana = Math.min((team1[1].mana || 0) + 25, 100);
                }
            },

            // D070 Molting Skin: 50% resistance to first negative status suffered this battle
            'D070-0000-0000-0000-000000000070'(team1, team2, index1, index2) {
                const card = team1[index1];
                card._moltingSkinActive = true;
            },

            // D141 Opening Bell: first allied card +25 Speed on first turn
            'D141-0000-0000-0000-000000000141'(team1, team2, index1, index2) {
                if (index1 === 0) {
                    team1[0]._openingBellSpeed = 25;
                    team1[0].speed += 25;
                }
            },

            // D142 Second Bell: second allied card +20 Speed for 2 turns on entry
            'D142-0000-0000-0000-000000000142'(team1, team2, index1, index2) {
                if (index1 === 1) {
                    team1[1].speed += 20;
                    team1[1]._secondBellTurns = 2;
                }
            },

            // D008 No-Fall Opener: first card with no negative statuses → 10% resurrection chance
            'D008-0000-0000-0000-000000000008'(team1, team2, index1, index2) {
                const card = team1[index1];
                card._noFallOpenerEligible = true;
            },

            // D183 Royal Audience: Warlord/Tactician in slot 1 → slot 2 +10 Accuracy +10 Speed
            'D183-0000-0000-0000-000000000183'(team1, team2, index1, index2) {
                if (team1.length > 1) {
                    const leader = team1[0];
                    if (leader.trait && (leader.trait.toLowerCase().includes('warlord') || leader.trait.toLowerCase().includes('tactician'))) {
                        team1[1].accuracy += 10;
                        team1[1].speed += 10;
                    }
                }
            },

            // D140 Null School: no mental-status unlocks → 20% resistance to Confused, Hallucinating, Dominated
            'D140-0000-0000-0000-000000000140'(team1, team2, index1, index2) {
                team1.forEach(card => { card._nullSchoolActive = true; });
            },

            // D088 Shield Relay: first enemy Magic Attack → redirect to highest Ward ally; first Physical → highest Armour
            'D088-0000-0000-0000-000000000088'(team1, team2, index1, index2) {
                team1[index1]._shieldRelayActive = true;
            },

            // D097 Escort Formation: second allied card takes 20% less damage while first is alive
            'D097-0000-0000-0000-000000000097'(team1, team2, index1, index2) {
                if (index1 === 1 && team1[0] && team1[0].hp > 0) {
                    team1[1]._escortFormationActive = true;
                }
            },

            // D160 Final Countdown: final card's 'next turn' delay clauses -1 turn
            'D160-0000-0000-0000-000000000160'(team1, team2, index1, index2) {
                const card = team1[index1];
                card._finalCountdownActive = true;
            },

            // D030 Reservist's Claim: final allied card gains 15% of each prior ally's highest stat
            'D030-0000-0000-0000-000000000030'(team1, team2, index1, index2) {
                // Applied at succession time when card enters as final
            },

            // D186 Toxic Crown: Venomancer lands first hit → all Poison durations +1 this match
            'D186-0000-0000-0000-000000000186'(team1, team2, index1, index2) {
                const card = team1[index1];
                if (card.trait && card.trait.toLowerCase().includes('venomancer')) {
                    card._toxicCrownActive = true;
                }
            }
        },

        // ─────────────────────────────────────────────
        // TURN — per-turn passive effects
        // ─────────────────────────────────────────────
        turn: {

            // D011 Overflow Tithe: Stamina > 100 → 50% excess becomes Mana for team
            'D011-0000-0000-0000-000000000011'(team1, team2, index1, index2) {
                team1.forEach(card => {
                    if (card.stamina > 100) {
                        const excess = card.stamina - 100;
                        const manaGain = Math.floor(excess * 0.5);
                        card.stamina = 100;
                        team1.forEach(c => { c.mana = Math.min((c.mana || 0) + Math.floor(manaGain / team1.length), 100); });
                    }
                });
            },

            // D012 Arc Battery: Mana > 100 → 50% excess becomes Stamina for team
            'D012-0000-0000-0000-000000000012'(team1, team2, index1, index2) {
                team1.forEach(card => {
                    if (card.mana > 100) {
                        const excess = card.mana - 100;
                        const stamGain = Math.floor(excess * 0.5);
                        card.mana = 100;
                        team1.forEach(c => { c.stamina = Math.min((c.stamina || 0) + Math.floor(stamGain / team1.length), 100); });
                    }
                });
            },

            // D016 Pressure Tax: each enemy status reduces enemy Stamina/Mana gain by 15%
            'D016-0000-0000-0000-000000000016'(team1, team2, index1, index2) {
                const activeEnemy = team2[index2];
                const statusCount = activeEnemy.statuses ? activeEnemy.statuses.length : 0;
                if (statusCount > 0) {
                    activeEnemy._pressureTaxReduction = Math.min(statusCount * 15, 75);
                }
            },

            // D020 Emergency Siphon: lowest Stamina ally steals 5 from highest
            'D020-0000-0000-0000-000000000020'(team1, team2, index1, index2) {
                if (team1.length < 2) return;
                let lowestIdx = 0, highestIdx = 0;
                team1.forEach((c, i) => {
                    if ((c.stamina || 0) < (team1[lowestIdx].stamina || 0)) lowestIdx = i;
                    if ((c.stamina || 0) > (team1[highestIdx].stamina || 0)) highestIdx = i;
                });
                if (lowestIdx !== highestIdx && (team1[highestIdx].stamina || 0) >= 5) {
                    team1[highestIdx].stamina -= 5;
                    team1[lowestIdx].stamina = Math.min((team1[lowestIdx].stamina || 0) + 5, 100);
                }
            },

            // D072 Plague Dividend: each different enemy status → +3% attack for all allies
            'D072-0000-0000-0000-000000000072'(team1, team2, index1, index2) {
                const enemyStatuses = new Set();
                team2.forEach(card => { if (card.statuses) card.statuses.forEach(s => enemyStatuses.add(s)); });
                const bonus = enemyStatuses.size * 3;
                if (bonus > 0) {
                    team1.forEach(card => { card._plagueDividendBonus = bonus; });
                }
            },

            // D080 Toxic Mortgage: Poisoned enemies do 10% less damage
            'D080-0000-0000-0000-000000000080'(team1, team2, index1, index2) {
                const enemy = team2[index2];
                if (enemy.statuses && enemy.statuses.includes('poisoned')) {
                    enemy._toxicMortgagePenalty = true;
                }
            },

            // D074 Salt the Earth: Rooted enemies lose 10 Stamina and 10 Mana each turn
            'D074-0000-0000-0000-000000000074'(team1, team2, index1, index2) {
                const enemy = team2[index2];
                if (enemy.statuses && enemy.statuses.includes('rooted')) {
                    enemy.stamina = Math.max(0, (enemy.stamina || 0) - 10);
                    enemy.mana = Math.max(0, (enemy.mana || 0) - 10);
                }
            },

            // D065 Mind Fog: Hallucinating enemies lose 10% Accuracy per turn active
            'D065-0000-0000-0000-000000000065'(team1, team2, index1, index2) {
                const enemy = team2[index2];
                if (enemy.statuses && enemy.statuses.includes('hallucinating')) {
                    enemy.accuracy = Math.max(0, enemy.accuracy - 10);
                }
            },

            // D145 Lockstep: all allies >70 Speed → +5% accuracy
            'D145-0000-0000-0000-000000000145'(team1, team2, index1, index2) {
                const allFast = team1.every(c => c.speed > 70);
                if (allFast) {
                    team1.forEach(c => { c._lockstepBonus = 5; });
                }
            },

            // D146 Heavy March: all allies <70 Speed → +10% armour and ward
            'D146-0000-0000-0000-000000000146'(team1, team2, index1, index2) {
                const allSlow = team1.every(c => c.speed < 70);
                if (allSlow) {
                    team1.forEach(c => { c._heavyMarchBonus = 10; });
                }
            },

            // D148 Clock Theft: team applies Staggered → active ally +10 Speed
            'D148-0000-0000-0000-000000000148'(team1, team2, index1, index2) {
                const card = team1[index1];
                card._clockTheftActive = true;
            },

            // D153 Compressed Round: round 4+ → all Speed +10%
            'D153-0000-0000-0000-000000000153'(team1, team2, index1, index2, round) {
                if (round >= 4) {
                    team1.forEach(c => { c.speed = Math.round(c.speed * 1.10); });
                }
            },

            // D159 Reaction Tax: enemy reactive perks -10% activation chance if ally acted first
            'D159-0000-0000-0000-000000000159'(team1, team2, index1, index2) {
                team1[index1]._reactionTaxActive = true;
            },

            // D098 Rear Guard Hook: final allied card contributes 10% of its Attack to active ally
            'D098-0000-0000-0000-000000000098'(team1, team2, index1, index2) {
                const finalCard = team1[team1.length - 1];
                if (finalCard && team1.length > 1 && index1 !== team1.length - 1) {
                    team1[index1]._rearGuardBonus = Math.round(finalCard.attack * 0.10);
                }
            },

            // D028 Funeral Drum: ally dies → all remaining +5% attack (stacks up to 3x)
            'D028-0000-0000-0000-000000000028'(team1, team2, index1, index2) {
                // Applied in succession handler
                team1[index1]._funeralDrumActive = true;
            },

            // D075 Grim Discipline: DOT effects deal 5% more base damage
            'D075-0000-0000-0000-000000000075'(team1, team2, index1, index2) {
                team1[index1]._grimDisciplineActive = true;
            },

            // D121 Brute Doctrine: Brute/Juggernaut/Ravager traits +10% Broken effect strength
            'D121-0000-0000-0000-000000000121'(team1, team2, index1, index2) {
                team1.forEach(card => {
                    if (card.trait && ['brute','juggernaut','ravager'].some(t => card.trait.toLowerCase().includes(t))) {
                        card._bruteDoctrine = true;
                    }
                });
            },

            // D130 Warpath Clause: Vanguard/Warlord/Tactician restore 5 Stamina on entry
            'D130-0000-0000-0000-000000000130'(team1, team2, index1, index2) {
                const card = team1[index1];
                if (card.trait && ['vanguard','warlord','tactician'].some(t => card.trait.toLowerCase().includes(t))) {
                    card.stamina = Math.min((card.stamina || 0) + 5, 100);
                }
            },

            // D131 Ritual Circle: Ritualist/Enchanter/Invoker → +1 turn on self-generated positive effects
            'D131-0000-0000-0000-000000000131'(team1, team2, index1, index2) {
                const card = team1[index1];
                if (card.trait && ['ritualist','enchanter','invoker'].some(t => card.trait.toLowerCase().includes(t))) {
                    card._ritualCircleActive = true;
                }
            }
        },

        // ─────────────────────────────────────────────
        // MOVE — on-attack / on-hit effects
        // ─────────────────────────────────────────────
        move: {

            // D056 One Clean Hit: first Crit on each enemy also applies Marked
            'D056-0000-0000-0000-000000000056'(team1, team2, index1, index2, damage, isCrit) {
                if (isCrit) {
                    const enemy = team2[index2];
                    if (!enemy._oneCleanHitApplied) {
                        enemy._oneCleanHitApplied = true;
                        if (!enemy.statuses) enemy.statuses = [];
                        if (!enemy.statuses.includes('marked')) enemy.statuses.push('marked');
                    }
                }
            },

            // D045 Merciless Geometry: attacks against Marked enemies +15% Crit Chance
            'D045-0000-0000-0000-000000000045'(team1, team2, index1, index2) {
                const enemy = team2[index2];
                if (enemy.statuses && enemy.statuses.includes('marked')) {
                    team1[index1]._mercilessGeometryBonus = 15;
                }
            },

            // D061 Ash Chain: Burning applied → 25% chance to also apply Overheated
            'D061-0000-0000-0000-000000000061'(team1, team2, index1, index2, appliedStatus) {
                if (appliedStatus === 'burning' && Math.random() < 0.25) {
                    const enemy = team2[index2];
                    if (!enemy.statuses) enemy.statuses = [];
                    if (!enemy.statuses.includes('overheated')) enemy.statuses.push('overheated');
                }
            },

            // D062 Rot Bloom: Poisoned applied → 20% chance to also apply Withered
            'D062-0000-0000-0000-000000000062'(team1, team2, index1, index2, appliedStatus) {
                if (appliedStatus === 'poisoned' && Math.random() < 0.20) {
                    const enemy = team2[index2];
                    if (!enemy.statuses) enemy.statuses = [];
                    if (!enemy.statuses.includes('withered')) enemy.statuses.push('withered');
                }
            },

            // D064 Cold Snap: first Slowed on each enemy also removes their Speed buff
            'D064-0000-0000-0000-000000000064'(team1, team2, index1, index2, appliedStatus) {
                if (appliedStatus === 'slowed') {
                    const enemy = team2[index2];
                    if (!enemy._coldSnapApplied) {
                        enemy._coldSnapApplied = true;
                        if (enemy.speedBuff) { enemy.speed -= enemy.speedBuff; enemy.speedBuff = 0; }
                    }
                }
            },

            // D068 Signal Corruption: Jammed enemies have 20% chance to lose one active buff
            'D068-0000-0000-0000-000000000068'(team1, team2, index1, index2) {
                const enemy = team2[index2];
                if (enemy.statuses && enemy.statuses.includes('jammed') && Math.random() < 0.20) {
                    if (enemy.buffs && Object.keys(enemy.buffs).length > 0) {
                        const key = Object.keys(enemy.buffs)[0];
                        delete enemy.buffs[key];
                    }
                }
            },

            // D082 Siege Ladder: basic attacks deal 5% splash to next enemy in line
            'D082-0000-0000-0000-000000000082'(team1, team2, index1, index2, damage) {
                const nextIdx = index2 + 1;
                if (team2[nextIdx] && team2[nextIdx].hp > 0) {
                    team2[nextIdx].hp = Math.max(0, team2[nextIdx].hp - Math.round(damage * 0.05));
                }
            },

            // D083 Backline Harass: Magic Attacks deal 5% spill to final enemy
            'D083-0000-0000-0000-000000000083'(team1, team2, index1, index2, damage) {
                const finalIdx = team2.length - 1;
                if (finalIdx !== index2 && team2[finalIdx] && team2[finalIdx].hp > 0) {
                    team2[finalIdx].hp = Math.max(0, team2[finalIdx].hp - Math.round(damage * 0.05));
                }
            },

            // D089 Bone Line: enemy <30% HP → 25% excess damage carries to next enemy
            'D089-0000-0000-0000-000000000089'(team1, team2, index1, index2, damage) {
                const enemy = team2[index2];
                const hpPct = (enemy.hp / enemy.maxHp) * 100;
                if (hpPct < 30 && damage > enemy.hp) {
                    const excess = damage - enemy.hp;
                    const carryover = Math.round(excess * 0.25);
                    const nextIdx = index2 + 1;
                    if (team2[nextIdx]) {
                        team2[nextIdx].hp = Math.max(0, team2[nextIdx].hp - carryover);
                    }
                }
            },

            // D093 Threat Priority: +15% damage vs highest-attack enemy
            'D093-0000-0000-0000-000000000093'(team1, team2, index1, index2, damage) {
                let maxAtk = 0, maxIdx = 0;
                team2.forEach((c, i) => { if ((c.attack || 0) > maxAtk) { maxAtk = c.attack; maxIdx = i; } });
                if (index2 === maxIdx) return Math.round(damage * 1.15);
                return damage;
            },

            // D094 Soft Priority: +10% damage vs lowest-HP enemy
            'D094-0000-0000-0000-000000000094'(team1, team2, index1, index2, damage) {
                let minHp = Infinity, minIdx = 0;
                team2.forEach((c, i) => { if ((c.hp || 0) < minHp && c.hp > 0) { minHp = c.hp; minIdx = i; } });
                if (index2 === minIdx) return Math.round(damage * 1.10);
                return damage;
            },

            // D048 Lucky Scar: crit received → next hit gains +20% Crit Chance, +10% Crit Effect
            'D048-0000-0000-0000-000000000048'(team1, team2, index1, index2) {
                team1[index1]._luckyScarActive = true;
            },

            // D049 Predicted Arc: acts before opponent → first hit +15% Crit Effect
            'D049-0000-0000-0000-000000000049'(team1, team2, index1, index2, damage, actedFirst) {
                if (actedFirst) {
                    return Math.round(damage * 1.15);
                }
                return damage;
            },

            // D057 Narrow Escape: enemy survives at <10% HP → next allied hit guaranteed crit
            'D057-0000-0000-0000-000000000057'(team1, team2, index1, index2, damage) {
                const enemy = team2[index2];
                const hpAfter = enemy.hp - damage;
                const hpPct = (hpAfter / enemy.maxHp) * 100;
                if (hpAfter > 0 && hpPct < 10) {
                    team1[index1]._narrowEscapeGuaranteedCrit = true;
                }
            },

            // D040 Finisher's Writ: final allied card +20% damage vs enemies already hit by prior allies
            'D040-0000-0000-0000-000000000040'(team1, team2, index1, index2, damage) {
                const card = team1[index1];
                const enemy = team2[index2];
                if (index1 === team1.length - 1 && enemy._damagedByPriorAlly) {
                    return Math.round(damage * 1.20);
                }
                return damage;
            },

            // D092 Mark Relay: Marked enemy dies → Marked transfers to next enemy for 1 turn
            'D092-0000-0000-0000-000000000092'(team1, team2, index1, index2, damage) {
                const enemy = team2[index2];
                if (enemy.statuses && enemy.statuses.includes('marked') && (enemy.hp - damage) <= 0) {
                    const nextIdx = index2 + 1;
                    if (team2[nextIdx]) {
                        if (!team2[nextIdx].statuses) team2[nextIdx].statuses = [];
                        team2[nextIdx].statuses.push('marked');
                        team2[nextIdx]._markRelayTurns = 1;
                    }
                }
            },

            // D033 Legacy Scar: prior ally inflicted status before dying → next ally +20% apply chance for that status
            'D033-0000-0000-0000-000000000033'(team1, team2, index1, index2) {
                team1[index1]._legacyScarActive = true;
            },

            // D122 Cutthroat Compact: Assassin/Predator/Duelist +10% damage vs Marked
            'D122-0000-0000-0000-000000000122'(team1, team2, index1, index2, damage) {
                const card = team1[index1];
                const enemy = team2[index2];
                if (card.trait && ['assassin','predator','duelist'].some(t => card.trait.toLowerCase().includes(t))) {
                    if (enemy.statuses && enemy.statuses.includes('marked')) {
                        return Math.round(damage * 1.10);
                    }
                }
                return damage;
            },

            // D128 Venom Pact: Venomancer/Hexer/Necrotic +10% damage vs Cursed/Withered enemies
            'D128-0000-0000-0000-000000000128'(team1, team2, index1, index2, damage) {
                const card = team1[index1];
                const enemy = team2[index2];
                if (card.trait && ['venomancer','hexer','necrotic'].some(t => card.trait.toLowerCase().includes(t))) {
                    if (enemy.statuses && (enemy.statuses.includes('cursed') || enemy.statuses.includes('withered'))) {
                        return Math.round(damage * 1.10);
                    }
                }
                return damage;
            },

            // D051 Soft Skull: Crits against Staggered enemies +15% damage
            'D051-0000-0000-0000-000000000051'(team1, team2, index1, index2, damage, isCrit) {
                const enemy = team2[index2];
                if (isCrit && enemy.statuses && enemy.statuses.includes('staggered')) {
                    return Math.round(damage * 1.15);
                }
                return damage;
            },

            // D060 Aimed Collapse: enemy Ward/Armour < 50% starting → +10% Crit Effect
            'D060-0000-0000-0000-000000000060'(team1, team2, index1, index2) {
                const enemy = team2[index2];
                const wardBroken = enemy.maxWard && (enemy.ward / enemy.maxWard) < 0.5;
                const armBroken = enemy.maxArmor && (enemy.armor / enemy.maxArmor) < 0.5;
                if (wardBroken || armBroken) {
                    team1[index1]._aimedCollapseBonus = 10;
                }
            },

            // D066 Cruel Mercy: Pacified enemies take +15% magic damage
            'D066-0000-0000-0000-000000000066'(team1, team2, index1, index2, damage, attackType) {
                const enemy = team2[index2];
                if (attackType === 'magic' && enemy.statuses && enemy.statuses.includes('pacified')) {
                    return Math.round(damage * 1.15);
                }
                return damage;
            },

            // D096 Kill Zone: enemies entering battle take 5% of stored spillover damage
            'D096-0000-0000-0000-000000000096'(team1, team2, index1, index2) {
                team1[index1]._killZoneActive = true;
            },

            // D100 Culling Draft: enemy retreats/swaps → enters next time with -10% Armour and Ward
            'D100-0000-0000-0000-000000000100'(team1, team2, index1, index2) {
                team1[index1]._cullingDraftActive = true;
            },

            // D174 Mirror Burn: reflected damage can apply on-hit statuses at 50% chance
            'D174-0000-0000-0000-000000000174'(team1, team2, index1, index2) {
                team1[index1]._mirrorBurnActive = true;
            },

            // D081 Hunter's Compass: enemy Marked → 60% chance to call ally for one bonus attack
            'D081-0000-0000-0000-000000000081'(team1, team2, index1, index2) {
                const enemy = team2[index2];
                if (enemy.statuses && enemy.statuses.includes('marked') && Math.random() < 0.60) {
                    team1[index1]._huntersCompassBonus = true;
                }
            },

            // D147 Borrowed Turn: first time defeating an enemy → +15 Speed until end of next turn
            'D147-0000-0000-0000-000000000147'(team1, team2, index1, index2, damage) {
                const enemy = team2[index2];
                if ((enemy.hp - damage) <= 0 && !team1[index1]._borrowedTurnUsed) {
                    team1[index1].speed += 15;
                    team1[index1]._borrowedTurnUsed = true;
                    team1[index1]._borrowedTurnExpiry = 2;
                }
            }
        },

        // ─────────────────────────────────────────────
        // DAMAGED — on-taking-damage effects
        // ─────────────────────────────────────────────
        damaged: {

            // D003 Second Wind Threshold: team takes 4+ crits → all allies +25% Crit Damage
            'D003-0000-0000-0000-000000000003'(team1, team2, index1, index2, damage, isCrit) {
                if (isCrit) {
                    team1[index1]._critsTaken = (team1[index1]._critsTaken || 0) + 1;
                    const totalCrits = team1.reduce((sum, c) => sum + (c._critsTaken || 0), 0);
                    if (totalCrits >= 4) {
                        team1.forEach(c => { c._secondWindActive = true; });
                    }
                }
            },

            // D005 Panic Drain: team takes 3+ crits → enemy -20% Accuracy
            'D005-0000-0000-0000-000000000005'(team1, team2, index1, index2, damage, isCrit) {
                if (isCrit) {
                    team1[index1]._critsTaken = (team1[index1]._critsTaken || 0) + 1;
                    const totalCrits = team1.reduce((sum, c) => sum + (c._critsTaken || 0), 0);
                    if (totalCrits > 3) {
                        team2.forEach(c => { c.accuracy = Math.max(0, c.accuracy - 20); });
                    }
                }
            },

            // D006 Adrenal Surge: team takes 3+ crits → all allies +20% Speed
            'D006-0000-0000-0000-000000000006'(team1, team2, index1, index2, damage, isCrit) {
                if (isCrit) {
                    team1[index1]._critsTaken = (team1[index1]._critsTaken || 0) + 1;
                    const totalCrits = team1.reduce((sum, c) => sum + (c._critsTaken || 0), 0);
                    if (totalCrits > 3) {
                        team1.forEach(c => { c.speed = Math.round(c.speed * 1.20); });
                    }
                }
            },

            // D104 Hard Return: 25% of Armour-prevented Physical damage → reflected chip damage
            'D104-0000-0000-0000-000000000104'(team1, team2, index1, index2, prevented) {
                const chip = Math.round(prevented * 0.25);
                if (chip > 0) {
                    team2[index2].hp = Math.max(0, team2[index2].hp - chip);
                }
            },

            // D105 Arc Return: 25% of Ward-prevented Magic damage → reflected chip magic damage
            'D105-0000-0000-0000-000000000105'(team1, team2, index1, index2, prevented) {
                const chip = Math.round(prevented * 0.25);
                if (chip > 0) {
                    team2[index2].hp = Math.max(0, team2[index2].hp - chip);
                }
            },

            // D108 Aegis Bloom: Ward > Armour → restore 5 Mana first time hit each battle
            'D108-0000-0000-0000-000000000108'(team1, team2, index1, index2, damage) {
                const card = team1[index1];
                if (card.ward > card.armor && !card._aegisBloomUsed) {
                    card._aegisBloomUsed = true;
                    card.mana = Math.min((card.mana || 0) + 5, 100);
                }
            },

            // D112 Ward into Teeth: 30% of Magic damage mitigated by Ward → bonus Crit Damage on next Crit
            'D112-0000-0000-0000-000000000112'(team1, team2, index1, index2, prevented) {
                const card = team1[index1];
                card._wardIntoTeethBonus = (card._wardIntoTeethBonus || 0) + Math.round(prevented * 0.30);
            },

            // D113 Armour into Fury: 30% of Physical damage mitigated → bonus Physical Attack on next hit
            'D113-0000-0000-0000-000000000113'(team1, team2, index1, index2, prevented) {
                const card = team1[index1];
                card._armourIntoFuryBonus = (card._armourIntoFuryBonus || 0) + Math.round(prevented * 0.30);
            },

            // D119 Stone Reflex: dodge → +10% Armour until end of next turn
            'D119-0000-0000-0000-000000000119'(team1, team2, index1, index2, dodged) {
                if (dodged) {
                    const card = team1[index1];
                    card.armor = Math.round(card.armor * 1.10);
                    card._stoneReflexExpiry = 2;
                }
            },

            // D013 Blood Budget: first time below 50% HP → restore 10 Stamina and 10 Mana
            'D013-0000-0000-0000-000000000013'(team1, team2, index1, index2, damage) {
                const card = team1[index1];
                const hpAfter = card.hp - damage;
                const hpPct = (hpAfter / card.maxHp) * 100;
                if (hpPct < 50 && !card._bloodBudgetUsed) {
                    card._bloodBudgetUsed = true;
                    card.stamina = Math.min((card.stamina || 0) + 10, 100);
                    card.mana = Math.min((card.mana || 0) + 10, 100);
                }
            },

            // D041 Backlash Threshold: team takes 4+ crits → enemies take 25% reflected damage from their own crits
            'D041-0000-0000-0000-000000000041'(team1, team2, index1, index2, damage, isCrit) {
                if (isCrit) {
                    const totalCrits = team1.reduce((sum, c) => sum + (c._critsTaken || 0), 0) + 1;
                    if (totalCrits > 4) {
                        team2[index2].hp = Math.max(0, team2[index2].hp - Math.round(damage * 0.25));
                    }
                }
            },

            // D042 Thin Margin: team takes 5+ negative statuses → allied Crit Chance doubled
            'D042-0000-0000-0000-000000000042'(team1, team2, index1, index2) {
                const totalStatuses = team1.reduce((sum, c) => sum + (c.statuses ? c.statuses.length : 0), 0);
                if (totalStatuses >= 5) {
                    team1.forEach(c => { c._thinMarginActive = true; });
                }
            },

            // D069 Hex Reflection: ally cursed for first time → active enemy also cursed
            'D069-0000-0000-0000-000000000069'(team1, team2, index1, index2, appliedStatus) {
                if (appliedStatus === 'cursed') {
                    const card = team1[index1];
                    if (!card._hexReflectionUsed) {
                        card._hexReflectionUsed = true;
                        if (!team2[index2].statuses) team2[index2].statuses = [];
                        if (!team2[index2].statuses.includes('cursed')) team2[index2].statuses.push('cursed');
                    }
                }
            },

            // D109 Reversal Lattice: first debuff each card per battle → 15% chance to bounce back
            'D109-0000-0000-0000-000000000109'(team1, team2, index1, index2, appliedStatus) {
                const card = team1[index1];
                if (!card._reversalLatticeUsed && Math.random() < 0.15) {
                    card._reversalLatticeUsed = true;
                    if (!team2[index2].statuses) team2[index2].statuses = [];
                    team2[index2].statuses.push(appliedStatus);
                    return true; // signal: debuff bounced
                }
                return false;
            },

            // D114 Split Plate: first damage source -35%, then permanent -5% armour and ward
            'D114-0000-0000-0000-000000000114'(team1, team2, index1, index2, damage) {
                const card = team1[index1];
                if (!card._splitPlateUsed) {
                    card._splitPlateUsed = true;
                    card.armor = Math.max(0, card.armor - Math.round(card.armor * 0.05));
                    card.ward = Math.max(0, card.ward - Math.round(card.ward * 0.05));
                    return Math.round(damage * 0.65);
                }
                return damage;
            },

            // D120 Ablation Engine: buff expires naturally → +5% max-HP shield
            'D120-0000-0000-0000-000000000120'(team1, team2, index1, index2) {
                team1[index1]._ablationEngineActive = true;
            },

            // D047 Pressure Sight: allied attack misses → +12% Accuracy on next attack
            'D047-0000-0000-0000-000000000047'(team1, team2, index1, index2, missed) {
                if (missed) {
                    team1[index1].accuracy += 12;
                }
            },

            // D050 Precision Theft: enemy misses → steal 5% Accuracy (max 20% per battle)
            'D050-0000-0000-0000-000000000050'(team1, team2, index1, index2, enemyMissed) {
                if (enemyMissed) {
                    const card = team1[index1];
                    const stolen = Math.min(5, 20 - (card._precisionTheftTotal || 0));
                    if (stolen > 0) {
                        card.accuracy += stolen;
                        card._precisionTheftTotal = (card._precisionTheftTotal || 0) + stolen;
                        team2[index2].accuracy = Math.max(0, team2[index2].accuracy - stolen);
                    }
                }
            },

            // D076 Ablative Prayer: first Dominated attempt → 70% chance to reverse to opponent
            'D076-0000-0000-0000-000000000076'(team1, team2, index1, index2, appliedStatus) {
                if (appliedStatus === 'dominated') {
                    const card = team1[index1];
                    if (!card._ablativePrayerUsed && Math.random() < 0.70) {
                        card._ablativePrayerUsed = true;
                        if (!team2[index2].statuses) team2[index2].statuses = [];
                        team2[index2].statuses.push('dominated');
                        return true; // signal: reversed
                    }
                    card._ablativePrayerUsed = true;
                }
                return false;
            }
        }

    };

})();
